import React, { useState } from 'react'
import { Form, Button, Card, Modal, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  createPromotion,
  deletePromotion,
  updatePromotion
} from '../../../../../../../../crud/management_api/local_site_configuration'
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner'
import { LocalSiteConfig, Promotion } from '../../types'
import { FaPen, FaPlus, FaTrash } from 'react-icons/fa'
import './Promotions.scss'
import { X } from 'react-bootstrap-icons'

interface PromotionsProps {
  config: LocalSiteConfig
  setConfig: any
}

const Promotions: React.FC<PromotionsProps> = ({ config, setConfig }) => {
  const [showModal, setShowModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<Partial<Promotion>>({})
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined)

  const openCreateModal = () => {
    setForm({})
    setEditingPromotion(null)
    setImagePreview(undefined)
    setShowModal(true)
  }

  const openEditModal = (promo: Promotion) => {
    setForm({
      promotion_name: promo.promotion_name,
      promotion_description: promo.promotion_description,
      promotion_url: promo.promotion_url,
      promotion_image: promo.promotion_image,
    })
    setImagePreview(promo.promotion_image || undefined)
    setEditingPromotion(promo)
    setShowModal(true)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      setForm(f => ({ ...f, promotion_image: undefined }))
      setImagePreview(undefined)
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result as string
      setForm(f => ({ ...f, promotion_image: base64 }))
      setImagePreview(base64)
    }
    reader.readAsDataURL(file)
  }

  const handleAdd = async () => {
    const user = localStorage.getItem('user')
    const localId = user ? JSON.parse(user).local_id : null
    if (!localId) {
      toast.error('Error al obtener local')
      return
    }
    if (!form.promotion_name || !form.promotion_description) {
      toast.error('Nombre y descripción son obligatorios')
      return
    }
    setCreating(true)
    try {
      const res = await createPromotion(localId, form as any)
      setConfig((res as any).data.object)
      toast.success('Promoción agregada')
      setShowModal(false)
      setForm({})
      setImagePreview(undefined)
    } catch {
      toast.error('Error al crear promoción')
    } finally {
      setCreating(false)
    }
  }

  const handleUpdate = async () => {
    if (!editingPromotion) return
    const user = localStorage.getItem('user')
    const localId = user ? JSON.parse(user).local_id : null
    if (!localId) {
      toast.error('Error al obtener local')
      return
    }
    setCreating(true)
    try {
      const res = await updatePromotion(
        localId,
        editingPromotion.promotion_id,
        form as any
      )
      setConfig((res as any).data.object)
      toast.success('Promoción actualizada')
      setShowModal(false)
      setEditingPromotion(null)
      setForm({})
      setImagePreview(undefined)
    } catch {
      toast.error('Error al actualizar promoción')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    const user = localStorage.getItem('user')
    const localId = user ? JSON.parse(user).local_id : null
    try {
      const res = await deletePromotion(localId, id)
      setConfig((res as any).data.object)
      toast.success('Promoción eliminada')
    } catch {
      toast.error('Error al eliminar')
    }
  }

  return (
    <div className="promotions-manager-container">
      <h4>Promociones actuales</h4>
      <Table className='custom-table'>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {config.promotions.map(p => (
            <tr key={p.promotion_id}>
              <td>{p.promotion_name}</td>
              <td>{p.promotion_description}</td>
              <td className="action-button-table-container">
                <Button className="item-detail-button" variant="none" onClick={() => openEditModal(p)}>
                  <FaPen />
                </Button>
                <Button className="item-detail-button" variant="none" onClick={() => handleDelete(p.promotion_id)}>
                  <FaTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="create-btn-wrapper">
        <Button variant="none" onClick={openCreateModal}>
          <FaPlus /> Crear Promoción
        </Button>
      </div>

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        centered
        dialogClassName="promotions-modal"
      >
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>{editingPromotion ? 'Editar Promoción' : 'Crear Promoción'}</Modal.Title>
          </div>
          <Button variant="none" className="modal-close-button" onClick={() => setShowModal(false)}>
                        <X size={24} />
                      </Button>
        </Modal.Header>
        <Modal.Body>
          {creating && <MiniLoadingSpinner />}
          {!creating && (
            <Form>
              <Form.Group controlId="promoName">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={39}
                  value={form.promotion_name || ''}
                  onChange={e => setForm(f => ({ ...f, promotion_name: e.target.value }))}
                />
              </Form.Group>
              <Form.Group controlId="promoDesc" className="mt-2">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={43}
                  value={form.promotion_description || ''}
                  onChange={e => setForm(f => ({ ...f, promotion_description: e.target.value }))}
                />
              </Form.Group>
              <Form.Group controlId="promoUrl" className="mt-2">
                <Form.Label>URL (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={form.promotion_url || ''}
                  onChange={e => setForm(f => ({ ...f, promotion_url: e.target.value }))}
                />
              </Form.Group>
              {/* <Form.Group controlId="promoImage" className="mediopunto-multi-file-upload mt-2">
                <Form.Label>Imagen (opcional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <div className="upload-placeholder">
                  Haga clic para seleccionar una imagen o arrástrela aquí
                </div>
                <div className="file-preview">
                  {imagePreview && (
                    <div className="image-wrapper">
                      <img
                        src={imagePreview}
                        alt="Vista previa"
                        style={{ maxHeight: 150, borderRadius: 4 }}
                      />
                      <button
                        type="button"
                        className="remove-button"
                        onClick={() => {
                          setForm(f => ({ ...f, promotion_image: undefined }))
                          setImagePreview(undefined)
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </Form.Group> */}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="none"
            onClick={() => {
              setShowModal(false)
              setEditingPromotion(null)
              setForm({})
              setImagePreview(undefined)
            }}
            disabled={creating}
            className="form-button-cancel"
          >
            Cancelar
          </Button>
          {!creating && (
            <Button
              className="form-button-create"
              variant="none"
              onClick={editingPromotion ? handleUpdate : handleAdd}
              disabled={creating}
            >
              {editingPromotion ? 'Guardar' : 'Crear'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default Promotions
